import { useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import { isFreemiumTier, type FreemiumTier } from '@shared';

import { freemiumApi, freemiumKeys } from '@client/api/waterlily';
import { selectClient, selectFeatureFlags } from '@client/model/selectors';

// How long to keep polling for the unlock after returning from checkout before
// giving up (the webhook can lag, especially on a cold start).
const FINALIZING_POLL_WINDOW_MS = 30_000;
const FINALIZING_POLL_INTERVAL_MS = 2_000;

export function useFreemiumGate(clientId: string) {
  const advisorFeatureFlags = useSelector(selectFeatureFlags);
  const isFreemiumAdvisor = !!advisorFeatureFlags.freemiumAdvisor;

  // Sample clients are demos, not real clients — they get full (premium) access and
  // are never gated by freemium.
  const client = useSelector(selectClient);
  const isSampleClient = client?.clientId === clientId && !!client?.isSampleClient;

  // Stripe sends the advisor back with ?freemiumPaid=1 after a successful checkout;
  // the unlock arrives via webhook moments later, so poll until it lands.
  const [searchParams, setSearchParams] = useSearchParams();
  const justPaid = searchParams.get('freemiumPaid') === '1';
  const rawPaidTier = searchParams.get('fxTier');
  const paidTier: FreemiumTier | null =
    rawPaidTier && isFreemiumTier(rawPaidTier) ? rawPaidTier : null;
  const pollStartedAt = useRef(Date.now());

  const dismissPaidSuccess = useCallback(() => {
    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev);
        for (const key of ['freemiumPaid', 'fxStep', 'fxTier', 'fxIntent', 'fxAnswer']) {
          next.delete(key);
        }
        return next;
      },
      { replace: true },
    );
  }, [setSearchParams]);

  const { data, isLoading } = useQuery({
    queryKey: freemiumKeys.planningUnlock(clientId),
    queryFn: () => freemiumApi.getPlanningUnlock(clientId),
    enabled: isFreemiumAdvisor && !!clientId && !isSampleClient,
    refetchInterval: query => {
      const unlocked = query.state.data?.isUnlocked;
      const withinWindow = Date.now() - pollStartedAt.current < FINALIZING_POLL_WINDOW_MS;
      return justPaid && !unlocked && withinWindow ? FINALIZING_POLL_INTERVAL_MS : false;
    },
  });

  const isUnlocked = isSampleClient || (data?.isUnlocked ?? false);
  const handoffPending = data?.handoffPending ?? false;
  const isGated = isFreemiumAdvisor && !isUnlocked;

  return {
    isFreemiumAdvisor,
    isUnlocked,
    handoffPending,
    isGated,
    isLoading: isFreemiumAdvisor && isLoading,
    // Show a "finalizing" state instead of the paywall while the webhook settles.
    isFinalizing: justPaid && isGated && !handoffPending,
    // Once the unlock lands after a paid checkout, show the success screen until
    // the advisor continues into the app.
    showPaidSuccess: justPaid && isUnlocked && !!paidTier && !isSampleClient,
    paidTier,
    dismissPaidSuccess,
  };
}
